/**
 * Header Project Selector Component
 *
 * Compact film/project dropdown for the header navigation.
 * Displays a film icon with dropdown to switch between user's projects.
 *
 * Design specs from Figma (node 3762:8648):
 * - Film icon: 24x24px, color #b0b0b0 (gray-300)
 * - Dropdown chevron: 16x16px, color #b0b0b0
 * - Gap between icon and chevron: 10px
 * - Hover state: opacity change
 *
 * Dependencies:
 * - Popover from components/ui/popover (existing)
 * - Clapperboard, ChevronDown from lucide-react (existing)
 * - ProjectOption from types/orcamentos.interface.ts (existing)
 */

import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Clapperboard, ChevronDown, Check } from "lucide-react";
import { IProject } from "@/interfaces/project.interface";
import { cn } from "@/lib/class-control";

interface HeaderProjectSelectorProps {
  /** Available projects for selection */
  projects: IProject[];
  /** Currently selected project (null if none selected) */
  selectedProject: IProject | null;
  /** Callback when project changes */
  onProjectChange: (project: IProject) => void;
  /** Loading state */
  isLoading?: boolean;
}

export function HeaderProjectSelector({
  projects,
  selectedProject,
  onProjectChange,
  isLoading = false,
}: HeaderProjectSelectorProps) {
  const [open, setOpen] = useState(false);

  const handleSelectProject = (project: IProject) => {
    onProjectChange(project);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "flex items-center gap-[10px] hover:opacity-80 transition-opacity",
            isLoading && "opacity-50 cursor-wait"
          )}
          aria-label="Selecionar projeto"
          disabled={isLoading}
        >
          <Clapperboard className="h-6 w-6 text-gray-300" />
          <ChevronDown
            className={cn(
              "h-4 w-4 text-gray-300 transition-transform",
              open && "rotate-180"
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 bg-[#15203C] border-gray-600 p-2"
      >
        <div className="flex flex-col gap-1">
          {/* Header */}
          <div className="px-2 py-1.5 text-xs font-semibold text-gray-300 uppercase">
            Projetos
          </div>

          {/* Project List */}
          {isLoading ? (
            <div className="px-2 py-3 text-sm text-gray-300 text-center">
              Carregando projetos...
            </div>
          ) : projects.length === 0 ? (
            <div className="px-2 py-3 text-sm text-gray-300 text-center">
              Nenhum projeto disponível
            </div>
          ) : (
            projects.map((project) => {
              const isSelected = selectedProject?.id === project.id;
              return (
                <button
                  key={String(project.id)}
                  onClick={() => handleSelectProject(project)}
                  className={cn(
                    "flex items-center justify-between w-full px-2 py-2 text-left rounded-md transition-colors",
                    isSelected
                      ? "bg-purple-500/20 text-white"
                      : "text-gray-50 hover:bg-white/10"
                  )}
                >
                  <span className="text-sm font-medium truncate">
                    {project.name}
                  </span>
                  {isSelected && (
                    <Check className="h-4 w-4 text-purple-500 flex-shrink-0" />
                  )}
                </button>
              );
            })
          )}

          {/* No selection indicator */}
          {!selectedProject && projects.length > 0 && (
            <div className="mt-1 px-2 py-1 text-xs text-gray-400 border-t border-gray-600">
              Selecione um projeto para continuar
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
